import { Editor, Extension, Range } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import Suggestion, { SuggestionProps } from '@tiptap/suggestion';

import { isSlashTriggerBoundary, isSlashTriggerDismissed, slashCommandsPluginKey } from './slashCore';
import { filterSlashItems } from './slashSearch';
import { createSlashSuggestionRender } from './slashSuggestionRenderer';
import type { SlashItem } from './slashTypes';

export {
  forceRemoveStaleSlashMenuUi,
  closeSlashSuggestion,
  getEditorViewSafe,
  hasPendingSlashTrigger,
  isEditorViewReady,
  isSlashDebugEnabled,
  isSlashMenuVisible,
  isSlashReplayEnabled,
  isSlashRetryEnabled,
  replaySlashTokenAtCursor,
  slashCommandsPluginKey,
} from './slashCore';
export type { SlashItem } from './slashTypes';

const isSlashAllowed = (props: { editor: Editor; state: SuggestionProps['editor']['state']; range: Range }) => {
  const { state } = props;
  const { $from } = state.selection;
  const parent = $from.parent;
  if (!parent.isTextblock) return false;
  if (parent.type.name === 'codeBlock') return false;
  if (isSlashTriggerDismissed(props.editor, props.range.from)) return false;

  const textBeforeSlash = state.doc.textBetween($from.start(), props.range.from, '\n', '\n');
  return isSlashTriggerBoundary(textBeforeSlash);
};

export const createSlashExtension = (items: SlashItem[]) =>
  Extension.create({
    name: 'slashCommands',
    addOptions() {
      return {
        suggestion: {
          char: '/',
          allowedPrefixes: null,
          allow: isSlashAllowed,
          pluginKey: slashCommandsPluginKey,
          items: ({ query }: { query: string }) => filterSlashItems(items, query),
          command: ({ editor, range, props }: { editor: Editor; range: Range; props: SlashItem }) => {
            props.command({ editor, range });
          },
          render: createSlashSuggestionRender,
        },
      };
    },
    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            decorations(state) {
              const slashState = slashCommandsPluginKey.getState(state) as { active?: boolean; range?: Range } | null;
              if (!slashState?.active || !slashState.range) return null;
              const { from, to } = slashState.range;
              const end = Math.max(to, from + 1);
              return DecorationSet.create(state.doc, [Decoration.inline(from, end, { class: 'slash-menu__trigger' })]);
            },
          },
        }),
        Suggestion({ editor: this.editor, ...this.options.suggestion }),
      ];
    },
  });
